/**
 * Coverage Map Component — interactive SVG US map with facility dots
 * Usage in Alpine: x-data="coverageMap()"
 */
import { geoAlbersUsa, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import us from 'us-atlas/states-10m.json';
import initLucideIcons from './lucide-icons.js';

const WIDTH = 960;
const HEIGHT = 600;
const OVERLAP_DISTANCE = 10;
const LEGEND_WIDTH = 290;
const LEGEND_EDGE_GUTTER = 16;

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

function getLegendAnchorPosition(svg, host, x, y) {
    const svgRect = svg.getBoundingClientRect();
    const hostRect = host.getBoundingClientRect();
    const scale = Math.min(svgRect.width / WIDTH, svgRect.height / HEIGHT);
    const insetX = (svgRect.width - (WIDTH * scale)) / 2;
    const insetY = (svgRect.height - (HEIGHT * scale)) / 2;

    return {
        x: (svgRect.left - hostRect.left) + insetX + (x * scale),
        y: (svgRect.top - hostRect.top) + insetY + (y * scale),
        hostWidth: hostRect.width,
        hostHeight: hostRect.height,
    };
}

function escapeHtml(value) {
    return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
}

function getCoverageMapConfig() {
    const root = window.crmsThemeData;
    if (!root || typeof root !== 'object') {
        return {
            facilities: [],
            viewLocationLabel: 'View Location',
            railroadSuffix: 'served',
        };
    }

    const coverageMap = root.coverageMap;
    if (!coverageMap || typeof coverageMap !== 'object') {
        return {
            facilities: [],
            viewLocationLabel: 'View Location',
            railroadSuffix: 'served',
        };
    }

    const facilities = Array.isArray(coverageMap.facilities)
        ? coverageMap.facilities
        : [];

    const viewLocationLabel = String(coverageMap.viewLocationLabel || '').trim() || 'View Location';
    const railroadSuffix = String(coverageMap.railroadSuffix || '').trim() || 'served';

    return {
        facilities,
        viewLocationLabel,
        railroadSuffix,
    };
}

function projectFacilities(facilities, projection, isMobile) {
    const projected = [];

    facilities.forEach((facility) => {
        const lat = Number.parseFloat(String(facility.lat ?? ''));
        const lng = Number.parseFloat(String(facility.lng ?? ''));
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            return;
        }

        const coords = projection([lng, lat]);
        if (!coords) {
            return;
        }

        const [originX, originY] = coords;
        projected.push({
            facility,
            originX,
            originY,
            x: originX,
            y: originY,
            isOffset: false,
        });
    });

    const groups = [];
    projected.forEach((point) => {
        const existingGroup = groups.find((group) => group.some((candidate) => (
            Math.hypot(candidate.originX - point.originX, candidate.originY - point.originY) < OVERLAP_DISTANCE
        )));

        if (existingGroup) {
            existingGroup.push(point);
            return;
        }

        groups.push([point]);
    });

    const offsetRadius = isMobile ? 16 : 11;
    const pairVerticalOffset = isMobile ? 12 : 8;
    groups.forEach((group) => {
        if (group.length <= 1) {
            return;
        }

        group.forEach((point, index) => {
            if (group.length === 2) {
                const direction = index === 0 ? -1 : 1;
                point.x = point.originX;
                point.y = point.originY + (direction * pairVerticalOffset);
                point.isOffset = true;
                return;
            }

            const angle = (-Math.PI / 2) + ((Math.PI * 2) / group.length) * index;
            point.x = point.originX + (Math.cos(angle) * offsetRadius);
            point.y = point.originY + (Math.sin(angle) * offsetRadius);
            point.isOffset = true;
        });
    });

    return projected;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

export default function registerCoverageMap(Alpine) {
    Alpine.data('coverageMap', () => ({
        legend: { show: false, html: '', x: 0, y: 0, mobile: false },
        inquiryLink: '/contact',
        facilities: [],
        viewLocationLabel: 'View Location',
        railroadSuffix: 'served',
        activeDot: null,
        isMobile: false,

        updateViewportState() {
            this.isMobile = window.matchMedia('(max-width: 767px)').matches;
        },

        init() {
            const config = getCoverageMapConfig();
            this.facilities = config.facilities;
            this.viewLocationLabel = config.viewLocationLabel;
            this.railroadSuffix = config.railroadSuffix;
            this.inquiryLink = this.$el.dataset.mapInquiryLink || '/contact';
            this.updateViewportState();
            this._onViewportChange = () => this.updateViewportState();
            window.addEventListener('resize', this._onViewportChange);
            this.$nextTick(() => this.render());
        },

        destroy() {
            const container = this.$refs.mapContainer;
            if (container) container.innerHTML = '';
            if (this._onViewportChange) {
                window.removeEventListener('resize', this._onViewportChange);
            }
        },

        render() {
            const container = this.$refs.mapContainer;
            if (!container) return;
            container.innerHTML = ''; // Clear previous render (Turbo navigation)

            const projection = geoAlbersUsa()
                .scale(1280)
                .translate([WIDTH / 2, HEIGHT / 2]);

            const path = geoPath().projection(projection);

            const states = feature(us, us.objects.states);
            const dotRadius = this.isMobile ? 6 : 4;
            const pulseRadius = this.isMobile ? 8 : 6;
            const hitRadius = this.isMobile ? 14 : 10;

            // Create SVG
            const svg = document.createElementNS(SVG_NS, 'svg');
            svg.setAttribute('viewBox', `0 0 ${WIDTH} ${HEIGHT}`);
            svg.setAttribute('class', 'w-full h-full');
            svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

            // Render state paths
            states.features.forEach((state) => {
                const pathValue = path(state);
                if (!pathValue) {
                    return;
                }

                const statePath = document.createElementNS(SVG_NS, 'path');
                statePath.setAttribute('d', pathValue);
                statePath.setAttribute('fill', '#e7eef6');
                statePath.setAttribute('stroke', '#9eb1c6');
                statePath.setAttribute('stroke-width', '0.5');
                svg.appendChild(statePath);
            });

            // Offset only markers that collapse into the same on-screen area at national scale.
            const projectedFacilities = projectFacilities(this.facilities, projection, this.isMobile);

            projectedFacilities.forEach(({ facility, originX, originY, x: cx, y: cy, isOffset }) => {
                if (isOffset) {
                    const connector = document.createElementNS(SVG_NS, 'line');
                    connector.setAttribute('x1', originX);
                    connector.setAttribute('y1', originY);
                    connector.setAttribute('x2', cx);
                    connector.setAttribute('y2', cy);
                    connector.setAttribute('stroke', '#003366');
                    connector.setAttribute('stroke-width', this.isMobile ? '1.7' : '1.4');
                    connector.setAttribute('stroke-linecap', 'round');
                    connector.setAttribute('opacity', '0.42');
                    svg.appendChild(connector);
                }

                // Pulse ring
                const pulse = document.createElementNS(SVG_NS, 'circle');
                pulse.setAttribute('cx', cx);
                pulse.setAttribute('cy', cy);
                pulse.setAttribute('r', String(pulseRadius));
                pulse.setAttribute('fill', 'none');
                pulse.setAttribute('stroke', '#e10707');
                pulse.setAttribute('stroke-width', '1.5');
                pulse.setAttribute('opacity', '0.6');
                pulse.setAttribute('class', 'map-dot-pulse');
                svg.appendChild(pulse);

                // Main dot
                const dot = document.createElementNS(SVG_NS, 'circle');
                dot.setAttribute('cx', cx);
                dot.setAttribute('cy', cy);
                dot.setAttribute('r', String(dotRadius));
                dot.setAttribute('fill', '#e10707');
                dot.setAttribute('class', 'map-dot cursor-pointer');
                svg.appendChild(dot);

                // Expanded invisible hit area for touch devices
                const hitArea = document.createElementNS(SVG_NS, 'circle');
                hitArea.setAttribute('cx', cx);
                hitArea.setAttribute('cy', cy);
                hitArea.setAttribute('r', String(hitRadius));
                hitArea.setAttribute('fill', 'transparent');
                hitArea.setAttribute('class', 'cursor-pointer');
                svg.appendChild(hitArea);

                // Tooltip events
                const showLegend = () => {
                    const anchor = getLegendAnchorPosition(svg, this.$el, cx, cy);

                    if (this.activeDot) {
                        this.activeDot.classList.remove('map-dot-active');
                    }
                    dot.classList.add('map-dot-active');
                    this.activeDot = dot;

                    let html = `<div class="font-semibold text-navy text-sm">${escapeHtml(facility.name)}</div>`;
                    if (facility.railroad) {
                        html += `<div class="text-navy/70 mt-0.5 text-xs">${escapeHtml(facility.railroad)} ${escapeHtml(this.railroadSuffix)}</div>`;
                    }
                    if (facility.address) {
                        html += `<div class="text-navy/70 text-xs mt-1">${escapeHtml(facility.address)}</div>`;
                    }
                    if (facility.services?.length) {
                        html += `<div class="flex flex-wrap gap-1 mt-2">${facility.services.map((service) => `<span class="bg-red/10 text-red px-1.5 py-0.5 rounded text-xs leading-tight border border-red/20">${escapeHtml(service)}</span>`).join('')}</div>`;
                    }

                    const detailLink = facility.locationPath || this.inquiryLink;
                    html += '<div class="mt-3">';
                    html += `<a href="${escapeHtml(detailLink)}" class="group inline-flex w-full items-center justify-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-[0.12em] whitespace-nowrap transition-all duration-200 relative bg-navy text-white hover:bg-red">${escapeHtml(this.viewLocationLabel)} <i data-lucide="arrow-right" class="w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true"></i><span class="absolute bottom-0 left-0 w-full h-[3px] bg-red"></span></a>`;
                    html += '</div>';

                    this.legend.html = html;
                    this.legend.mobile = this.isMobile;
                    if (this.isMobile) {
                        this.legend.x = anchor.hostWidth / 2;
                        this.legend.y = anchor.hostHeight - 12;
                    } else {
                        const halfLegendWidth = LEGEND_WIDTH / 2;
                        const minX = Math.min(halfLegendWidth + LEGEND_EDGE_GUTTER, anchor.hostWidth / 2);
                        const maxX = Math.max(minX, anchor.hostWidth - halfLegendWidth - LEGEND_EDGE_GUTTER);

                        this.legend.x = clamp(anchor.x, minX, maxX);
                        this.legend.y = anchor.y - 12;
                    }
                    this.legend.show = true;
                    this.$nextTick(() => {
                        initLucideIcons();
                    });
                };

                hitArea.addEventListener('click', (event) => {
                    event.preventDefault();
                    showLegend();
                });
                hitArea.addEventListener('touchstart', (e) => {
                    e.preventDefault();
                    showLegend();
                }, { passive: false });
            });

            container.appendChild(svg);
        },

        closeLegend() {
            this.legend.show = false;
            if (this.activeDot) {
                this.activeDot.classList.remove('map-dot-active');
                this.activeDot = null;
            }
        },
    }));
}
